// Bài hướng dẫn trang Đăng ký ca — /ekip-shifts.
//
// Bài chia theo BA thẻ của màn. Nhãn thẻ lấy từ từ điển nhãn của màn, không gõ
// chuỗi lần hai — xem `../ekip-shift-guide-tabs.ts`.
//
// Điểm khó nhất khi viết bài này: trang có HAI mô hình sống song song, cố ý.
// Người khai ca từng tuần thì đăng ký ở thẻ đầu; người mặc định có mặt thì chỉ
// khai ngày nghỉ ở thẻ cuối. Bài phải nói rõ điều đó, vì hiểu nhầm mình thuộc
// nhóm nào là mất suất cả tuần mà không ai báo.
//
// ⚠️ Ranh giới thông tin ở `../types.ts` áp NGUYÊN VẸN.

import type { GuideBook } from "../types";
import { EKIP_SHIFT_TAB_LABEL } from "@/features/ekip-shift/labels";

export const EKIP_SHIFT_GUIDES: GuideBook = {
  ekip_shifts: {
    purpose:
      "Nơi khối chụp, trang điểm, trợ lý studio, kinh doanh và chăm sóc khách hàng khai tuần sau mình có mặt hay nghỉ.",
    where: [
      'Đầu trang: tiêu đề "📸 Đăng ký ca", dòng ngay dưới ghi tuần đang xem và cách tính ca của khối bạn.',
      'Cùng hàng tiêu đề: nút "+ Đăng ký"; hàng dưới là mũi tên đổi tuần, nút "Tuần này" và khoảng ngày của tuần.',
      "Số thẻ bạn thấy tuỳ theo công việc của bạn, nên hai người mở cùng trang có thể thấy khác nhau.",
    ],
    howItWorks: [
      "Mọi thứ trên trang xoay quanh MỘT tuần và MỘT khối; đổi tuần hay đổi khối là đổi toàn bộ bên dưới.",
      'Trang này khai sẵn sàng theo TUẦN, khác hẳn màn "Nhận đơn" là nơi nhận suất theo từng đơn hàng.',
      "Hai cách khai sống song song: người khai ca từng tuần thì đăng ký ca, người mặc định có mặt thì chỉ khai ngày nghỉ.",
      "Bạn thuộc cách nào thì trang tự bày đúng cách đó: không thấy thẻ đăng ký nghĩa là bạn mặc định có mặt cả tuần, chỉ cần khai ngày nghỉ — không phải trang hỏng hay bạn thiếu quyền.",
      "Tuần đã chốt thì không khai và không sửa ca được nữa.",
    ],
    statuses: [
      { label: "Chờ duyệt", meaning: "Đã khai, đang đợi người chốt lịch xem." },
      { label: "Đã duyệt", meaning: "Ca chính thức; từ đây máy mới xếp suất cho bạn." },
      { label: "Bị từ chối", meaning: "Không nhận ca này; lý do ghi ngay trên dòng." },
      { label: "Đã huỷ", meaning: "Ca đã bỏ, kèm lý do huỷ." },
      { label: "Đã khoá", meaning: "Tuần đã chốt, không khai và không sửa được nữa." },
    ],
    tabs: [
      {
        key: "register",
        label: EKIP_SHIFT_TAB_LABEL.register,
        purpose: "Lưới cả tuần của khối bạn, và chỗ khai ca mình sẽ làm.",
        where: [
          'Nút "+ Đăng ký" ở hàng tiêu đề; tuần đang chốt thì cạnh đó có thẻ đỏ "Đã khoá".',
          'Hàng dưới: mũi tên lùi và tiến tuần, nút "Tuần này", khoảng ngày, và dòng "Lý do khoá:" khi tuần đang chốt.',
          "Thanh lọc ngay trên lưới: ô chọn khối, ô \"Thương hiệu\" và ô chọn cơ sở — khối kinh doanh và khối chăm sóc khách hàng không có ô cơ sở; cuối thanh đó là nút thu nhỏ / phóng to lưới.",
          'Ô "Thương hiệu" có "Tất cả", "Chưa khai thương hiệu" và tên từng thương hiệu.',
          'Lưới bảy cột là bảy ngày, trục dọc là giờ. Cột hôm nay tô đậm và ghi chữ "Hôm nay"; ngày đã trôi qua thì mờ đi.',
          "Dưới số ngày ở đầu mỗi cột là số ca của ngày đó.",
          'Hàng nút "+ ..." dưới đáy lưới để khai thẳng một ca, khỏi mở hộp.',
          "Mỗi ca là một thẻ dài đúng bằng giờ làm, ghi tên người kèm thẻ màu tình trạng.",
          'Ca chưa điền giờ rơi xuống hàng "Chưa rõ giờ" ở đáy lưới.',
          'Tên ca tuỳ nơi làm: phía Nam có "Ca 1", "Ca 2", "Sáng PT", "Chiều PT", "Bù 8h"; phía Bắc có "Fulltime", "Ca 1", "Ca 2", "Ca 3"; khối kinh doanh và khối chăm sóc khách hàng mỗi khối một bộ ca riêng.',
          "Trong hộp khai ca: ngày làm, loại ca, giờ bắt đầu, giờ kết thúc, bộ phận, cơ sở và ghi chú.",
        ],
        howItWorks: [
          "Trang mở sẵn ở TUẦN SAU chứ không phải tuần này, vì việc thường ngày ở đây là khai trước cho tuần tới.",
          "Ca khai xong nằm ở chặng chờ; qua chặng sau thành ca chính thức thì máy mới xếp suất theo đó.",
          "Tới ngày làm, bạn vào ca ở ô Check-in trang Tổng quan đúng cơ sở, trong giờ ca, thì ca còn chờ tự chuyển sang đã duyệt — không phải đợi ai bấm (áp dụng khi bản mới lên web).",
          "Ai làm theo diện khai ca từng tuần mà chưa khai thì chưa có suất nào về tay — im lặng không phải là mặc định có mặt với nhóm này.",
          "Cùng một khối có thể trộn hai diện: lưới thiếu tên ai đó không có nghĩa họ nghỉ, có thể họ thuộc diện mặc định có mặt và chỉ khai ngày nghỉ.",
          'Ô "Thương hiệu" lọc lưới theo thương hiệu ghi trên hồ sơ của người khai ca, không theo đơn. Người làm cho nhiều thương hiệu hiện ở mọi thương hiệu họ có.',
          'Chọn một thương hiệu thì người chưa ghi thương hiệu nào trên hồ sơ ra khỏi lưới; chọn "Chưa khai thương hiệu" để xem riêng nhóm này.',
          "Ô này chỉ đổi cách xem lưới: thẻ Duyệt và số ca chờ vẫn tính đủ cả khối, và đổi tuần hay đổi khối thì lựa chọn vẫn giữ nguyên.",
          "Mỗi loại ca có giờ mặc định: giữ mặc định thì khỏi điền giờ, đổi giờ thì dòng ghi rõ là đã đổi.",
          "Ca dài vẽ thành thẻ cao, ca ngắn thẻ thấp — nhìn chiều cao là biết ai làm bao lâu mà không phải đọc giờ.",
          "Nhiều người cùng một khung giờ thì các thẻ đứng cạnh nhau, và ngày đó tự nong rộng ra để thẻ nào cũng đọc được tên; lưới rộng quá màn thì kéo ngang, hoặc bấm nút thu nhỏ để xem trọn tuần.",
          "Ca đã thành chính thức thì thu hẹp lại đúng khung giờ bạn khai.",
          "Chỉ dời được ca trong tuần hiện tại; sang tuần khác phải huỷ rồi khai lại.",
          "Chốt tuần đi theo KHỐI: khối ảnh chốt không làm khối trang điểm dừng theo, còn khối kinh doanh và khối chăm sóc khách hàng mỗi khối chốt một lượt cả nước.",
        ],
        statuses: [
          { label: "Chờ duyệt", meaning: "Đã khai, đang đợi người chốt lịch xem." },
          { label: "Đã duyệt", meaning: "Ca chính thức; từ đây máy mới xếp suất cho bạn." },
          { label: "Bị từ chối", meaning: "Không nhận ca này; lý do ghi ngay trên dòng." },
          { label: "Đã huỷ", meaning: "Ca đã bỏ, kèm lý do huỷ." },
          { label: "Chưa rõ giờ", meaning: "Ca chưa điền giờ bắt đầu và giờ kết thúc." },
          { label: "Đã khoá", meaning: "Tuần của khối này đã chốt." },
        ],
        blocks: [
          {
            for: ["photo", "makeup"],
            title: "Khai ca của bạn",
            steps: [
              "Kiểm tra bạn đang đứng đúng tuần, đúng khối và đúng cơ sở mình sẽ tới.",
              'Bấm ô trống trên lưới ở ngày và ca bạn làm, hoặc bấm "+ Đăng ký" rồi chọn tay.',
              "Muốn làm khác giờ mặc định thì điền giờ bắt đầu và giờ kết thúc theo dạng giờ hai chấm phút.",
              "Lưu xong theo dõi tới khi dòng chuyển sang đã duyệt — duyệt rồi máy mới xếp suất cho bạn.",
              "Tới ngày làm, vào ca ở ô Check-in trang Tổng quan đúng cơ sở trong giờ ca: ca còn chờ sẽ tự chuyển sang đã duyệt (áp dụng khi bản mới lên web).",
              "Bận: huỷ ca và ghi lý do, đừng để tới ngày mới báo.",
            ],
            troubles: [
              "Không bấm được: tuần của khối đó chưa mở hoặc đã chốt.",
              "Báo giờ bắt đầu phải trước giờ kết thúc: xem lại hai ô giờ.",
              "Ca bị từ chối: đọc lý do trên dòng rồi khai lại cho đúng.",
              "Đã khai mà trang suất vẫn trống: ca chưa thành chính thức, hoặc ngày đó chưa có khách đặt.",
              'Ca của bạn nằm ở hàng "Chưa rõ giờ": mở ca ra điền hai ô giờ.',
              "Lưới lạ hoắc, không thấy ca nào của mình: xem lại ô chọn khối trên thanh lọc.",
              'Máy báo "chồng giờ với khung …": hôm đó bạn đã có một ca khác trùng giờ (có thể ở cơ sở khác) — sửa giờ để hai ca nối tiếp nhau, hoặc huỷ ca kia trước.',
              'Đã khai mà không thấy ca mình trên lưới: xem ô "Thương hiệu" có đang để một thương hiệu khác không — đưa về "Tất cả".',
            ],
          },
          {
            for: ["qc"],
            title: "Đi làm nghề ngoài giờ kiểm",
            steps: [
              'Mở menu ảnh đại diện, chọn danh phận thợ ở mục "Dùng với tư cách". Dải màu ở đầu màn cho biết bạn đang dùng danh phận nào.',
              "Khai ca ở thẻ này như mọi thợ — ca khai lúc đang ở danh phận thợ mới là giờ làm nghề của bạn.",
              "Ca thành chính thức thì việc chụp hay trang điểm trong giờ ca đó được xếp cho bạn như mọi thợ.",
              'Xong việc thì bấm nút về trên dải màu để quay lại việc kiểm.',
            ],
            troubles: [
              'Không thấy mục "Dùng với tư cách": bạn chưa được gán danh phận thợ — nhắn quản lý trực tiếp.',
              "Ca khai lúc đang ở danh phận kiểm soát chất lượng không tính là giờ làm nghề: đổi sang danh phận thợ rồi khai ca đó.",
              "Đang dùng danh phận kiểm soát chất lượng phụ thì không khai ca được: đổi về danh phận chính rồi khai.",
            ],
          },
          {
            for: ["sale"],
            title: "Ca của khối kinh doanh",
            steps: [
              "Ca của bạn tính chung toàn quốc nên không cần chọn cơ sở.",
              "Khai như bình thường: chọn ngày, chọn ca rồi lưu.",
              "Khai ở đâu cũng vào cùng một bảng, không chia theo nơi làm.",
              'Tới ngày làm: "Ca 1" và "Ca 2" thì vào ca bằng nút "Vào ca tại văn phòng" ở ô Check-in trang Tổng quan; "Ca 3" thì vào ca tại văn phòng hay bấm "Vào ca làm từ xa" đều được.',
            ],
            troubles: [
              "Không thấy ô chọn cơ sở: đúng như vậy, ca của bạn tính chung toàn quốc nên máy ẩn ô đó đi.",
              "Thấy danh sách ca lạ hoắc: bạn đang để thanh lọc ở khối khác — chọn lại khối của mình, giờ ca hai bên khác nhau.",
              "Tuần bị chốt thì chốt cả nước một lượt, không chốt riêng từng nơi — cần đổi thì hỏi quản lý trực tiếp.",
            ],
          },
          {
            for: ["cskh"],
            title: "Ca của khối chăm sóc khách hàng",
            steps: [
              "Ca của bạn tính chung toàn quốc nên không cần chọn cơ sở — trong ca, khách của cơ sở nào cũng là khách của bạn.",
              'Có hai loại ca. Ca "Văn phòng" là ca bạn có mặt ở văn phòng. Năm ca "Trực 1", "Trực 2", "Trực 3", "Trực 4" và "Trực cả ngày" là ca trực kênh, ngồi ở văn phòng hay ở nhà đều được. Giờ của từng ca ghi ngay trong ô chọn ca.',
              'Tuần nào bạn lên văn phòng thì khai ca "Văn phòng" đúng những ngày đó, rồi khai thêm các ca Trực bạn nhận.',
              'Tới ngày làm: ca "Văn phòng" thì vào ca bằng nút "Vào ca tại văn phòng" ở ô Check-in trang Tổng quan; ca Trực thì vào ca tại văn phòng hay bấm "Vào ca làm từ xa" đều được.',
              "Một ngày khai được nhiều ca: ca Văn phòng chồng giờ với ca Trực là bình thường, không phải khai trùng.",
              "Khai như bình thường: chọn ngày, chọn ca rồi lưu.",
              "Để nguyên giờ mặc định của ca là đủ; chỉ điền hai ô giờ khi bạn làm khác giờ đó.",
              "Trong ca Trực của mình thì mọi việc khách phát sinh là việc của bạn, kể cả đơn không phải bạn theo từ đầu. Ca Văn phòng là ca có mặt, không phải ca trực kênh.",
              "Đổi ca với đồng nghiệp thì phải khai lại cho đúng, đừng để lịch ghi một đằng người trực một nẻo.",
            ],
            troubles: [
              "Không thấy ô chọn cơ sở: đúng như vậy, ca của bạn tính chung toàn quốc nên máy ẩn ô đó đi.",
              'Không thấy "Ca 1", "Ca 2", "Ca 3" trong ô chọn ca: ba ca cũ đó đã ngừng nhận khai mới — chọn ca Văn phòng hoặc một ca Trực. Ca cũ đã khai từ trước vẫn nằm trên lưới như thường.',
              'Máy báo "ca cũ này không còn nhận đăng ký": bạn đang khai lại một ca cũ — chọn ca Văn phòng hoặc một ca Trực.',
              "Thấy danh sách ca lạ hoắc: bạn đang để thanh lọc ở khối khác — chọn lại khối của mình, giờ ca các khối khác nhau.",
              'Ca của bạn nằm ở hàng "Chưa rõ giờ": mở ca ra điền hai ô giờ, vì ca thiếu giờ thì không ai biết lúc đó ai đang trực.',
              "Tuần bị chốt thì chốt cả nước một lượt, không chốt riêng từng nơi — cần đổi thì hỏi quản lý trực tiếp.",
            ],
          },
          {
            for: ["assistant"],
            title: "Ca của trợ lý studio",
            steps: [
              "Kiểm tra bạn đang đứng đúng tuần và đúng cơ sở mình sẽ tới — ca của bạn tính theo từng cơ sở, không tính chung toàn quốc.",
              'Bấm ô trống trên lưới ở ngày và ca bạn làm, hoặc bấm "+ Đăng ký" rồi chọn tay.',
              "Bộ ca của bạn dùng chung với khối ảnh ở cơ sở đó, nhưng giờ kết thúc nới muộn hơn vì bạn ở lại dọn sau buổi cuối.",
              "Làm khác giờ mặc định thì điền giờ bắt đầu và giờ kết thúc; ô chọn giờ của bạn có sẵn các mốc muộn đó.",
              "Lưu xong theo dõi tới khi dòng chuyển sang đã duyệt — giờ công của bạn đếm từ ca đã duyệt, ca còn chờ thì chưa đếm.",
              "Bận: huỷ ca và ghi lý do, đừng để tới ngày mới báo.",
            ],
            troubles: [
              "Không bấm được: tuần của khối đó chưa mở hoặc đã chốt.",
              "Không thấy mốc giờ muộn trong ô chọn: bạn đang để thanh lọc ở khối khác — chọn lại khối của mình.",
              "Báo giờ bắt đầu phải trước giờ kết thúc: xem lại hai ô giờ.",
              'Ca của bạn nằm ở hàng "Chưa rõ giờ": mở ca ra điền hai ô giờ, vì ca thiếu giờ thì không tính được giờ công.',
              "Ca bị từ chối: đọc lý do trên dòng rồi khai lại cho đúng.",
              'Máy báo "chồng giờ với khung …": hôm đó bạn đã có một ca khác trùng giờ (có thể ở cơ sở khác) — một giờ làm chỉ tính một lần; sửa giờ để hai ca nối tiếp nhau, hoặc huỷ ca kia trước.',
              "Không thấy nút chốt ca: việc đó không nằm ở bạn, quản lý trực tiếp ở cơ sở chốt.",
            ],
          },
          {
            for: ["lead", "exec", "qc"],
            title: "Nhìn tuần trước khi chốt",
            steps: [
              "Chọn khối cần xem trên thanh lọc, rồi mở tuần sắp tới.",
              'Muốn xem riêng người của một thương hiệu thì chọn thương hiệu đó ở ô "Thương hiệu".',
              "Nhìn lưới xem ngày nào mỏng người, ngày nào chưa ai khai.",
              "Thiếu người thì nhắc khai thêm trước khi chốt tuần.",
              'Thỉnh thoảng chọn "Chưa khai thương hiệu" xem ai còn thiếu thương hiệu trên hồ sơ, rồi nhờ người phụ trách hồ sơ nhân sự bổ sung.',
            ],
            troubles: [
              "Lưới trống ở một khối: xem lại ô chọn khối, ô \"Thương hiệu\" và ô chọn cơ sở trên thanh lọc trước khi kết luận không ai khai.",
              'Chọn thương hiệu mà vắng một người chắc chắn làm cho thương hiệu đó: hồ sơ của họ chưa ghi thương hiệu — tìm họ ở mục "Chưa khai thương hiệu".',
              'Dòng đỏ "Chưa đọc được thương hiệu của nhân sự": lưới đang hiện cả khối vì máy chưa lọc được — tải lại trang, còn lỗi thì báo bộ phận kỹ thuật.',
              "Chốt tuần ở khối này mà khối kia vẫn khai được: đúng như vậy, chốt đi theo từng khối.",
            ],
          },
        ],
      },
      {
        key: "approve",
        label: EKIP_SHIFT_TAB_LABEL.approve,
        purpose: "Danh sách ca đang chờ của khối, và chỗ chốt tuần khi đã đủ người.",
        where: [
          "Số trong ngoặc trên tên thẻ là số ca đang chờ của khối và tuần đang xem.",
          'Nút "Duyệt tất cả" ở đầu danh sách, kèm số ca sẽ được xử một lượt.',
          "Mỗi dòng: tên người khai, ngày, loại ca kèm giờ, bộ phận và cơ sở.",
          'Ca của khối trợ lý studio mà ngày đó người ấy còn có buổi chụp doanh nghiệp: cuối dòng có dấu vàng "⚠️ có buổi DN", và mở hộp ra thì có một dòng vàng nói rõ hơn.',
          "Bấm một dòng để mở hộp xem chi tiết và quyết từng ca.",
          'Ở hàng tiêu đề: nút "Khoá tuần", đổi thành "Mở khoá" khi tuần đang chốt.',
        ],
        howItWorks: [
          "Danh sách chỉ gồm ca của KHỐI đang xem: đứng ở khối khác thì không có nút quyết nào cả.",
          "Xử cả loạt chỉ chạm đúng khối, tuần và cơ sở đang xem, không lan sang chỗ khác.",
          "Xử xong có báo lại bao nhiêu ca được nhận, bao nhiêu ca bỏ qua — bỏ qua là ca đã được quyết từ trước.",
          "Không nhận một ca thì bắt buộc ghi lý do, vì người khai đọc đúng dòng đó để biết đường sửa.",
          "Câu xác nhận trước khi chốt hay xử loạt nói rõ phạm vi sẽ bị chạm; với khối kinh doanh phạm vi đó là cả nước.",
          "Ca còn chờ mà tuần đã chốt thì không thành ca chính thức — phải mở lại, xử nốt, rồi chốt lần nữa.",
          "Ca mà người khai đã vào ca đúng cơ sở trong giờ ca thì tự chuyển sang đã duyệt; danh sách chờ chỉ còn ca chưa ai vào ca và ca cần bạn xem kỹ. Ca tự duyệt vẫn huỷ được như ca bạn nhận (áp dụng khi bản mới lên web).",
          "Ca sắp tới ngày làm hoặc đã qua mà nằm chờ quá một ngày thì chuông nhắc bạn; để thêm hai ngày nữa thì chuông báo lên quản lý cấp cao (áp dụng khi bản mới lên web).",
          "Dấu vàng về buổi doanh nghiệp chỉ để nhắc: giờ làm ở buổi đó được trả riêng, nên nếu hai bên trùng khoảng thời gian thì bạn sửa giờ ca lại trước khi nhận. Máy không tự sửa giờ và không tự bớt gì.",
          "Dấu đó chỉ có ở khối trợ lý studio; khối khác không hiện vì giờ ca của họ không trả theo giờ.",
        ],
        statuses: [
          { label: "Chờ duyệt", meaning: "Ca đang đợi được quyết." },
          { label: "Đã duyệt", meaning: "Ca chính thức; máy bắt đầu xếp suất theo ca này." },
          { label: "Bị từ chối", meaning: "Ca không được nhận; lý do hiện trên dòng cho người khai đọc." },
          { label: "Đã huỷ", meaning: "Ca từng được nhận rồi bỏ, kèm lý do." },
          { label: "Đã khoá", meaning: "Tuần của khối này đã chốt." },
        ],
        blocks: [
          {
            // Không phát cho nhân viên thường: quy trình chốt ca là việc của
            // người có `ekipshift.approve`, và họ mang "lead"/"exec"/"qc".
            for: ["lead", "exec", "qc"],
            title: "Chốt ca của khối",
            steps: [
              "Chọn đúng khối trên thanh lọc rồi mở thẻ này.",
              "Rà danh sách chờ, bấm từng dòng để xem chi tiết hoặc xử cả loạt khi đã xem qua.",
              "Không nhận một ca thì ghi rõ lý do để người khai biết đường sửa.",
              "Quay lại lưới xem còn ngày nào mỏng người không.",
              "Đủ người thì chốt tuần cho khối đó.",
            ],
            troubles: [
              'Dòng có dấu "⚠️ có buổi DN": mở hộp ra, so giờ ca với buổi hôm đó. Trùng thật thì sửa giờ ca rồi mới nhận; không trùng thì nhận bình thường.',
              "Chốt tuần rồi mới thấy thiếu: mở lại, xử nốt rồi chốt lần nữa.",
              "Mở thẻ mà không có nút quyết nào: bạn đang đứng ở khối khác khối mình phụ trách — đổi ô chọn khối.",
              "Xử cả loạt xong vẫn còn dòng: những ca đó đã được quyết từ trước, hoặc trùng giờ với một ca đã nhận của cùng người, nên bị bỏ qua.",
              "Không nhận được một ca vì trùng giờ với ca đã nhận của cùng người (kể cả ở cơ sở khác): hai ca chỉ một là thật — huỷ ca kia hoặc không nhận ca này.",
              "Ca của chính bạn không tự nhận được, và đã nhận rồi thì không tự sửa giờ: để một người khác trong khối xử ca đó.",
              "Bấm Duyệt, Huỷ ca hay sửa giờ ca đã nhận mà máy báo tháng lương đã chốt: ca của tháng đó không đổi được nữa (xử cả loạt thì ca đó bị bỏ qua) — cần điều chỉnh thì hỏi kế toán.",
            ],
          },
        ],
      },
      {
        key: "absence",
        label: EKIP_SHIFT_TAB_LABEL.absence,
        purpose: "Chỗ khai ngày mình nghỉ, và chỗ xử đơn nghỉ của người khác.",
        where: [
          "Ô chọn ngày nghỉ và ô ghi lý do ở đầu thẻ.",
          'Ngay dưới đó là ô tích "Nghỉ theo khung giờ"; tích vào thì hiện thêm ô Từ giờ và ô Đến giờ.',
          'Người xử đơn có thêm mục "Chờ tôi duyệt": mỗi dòng kèm ngày, khung giờ, tên người xin, và hai nút "Duyệt", "Từ chối".',
          'Cuối thẻ là lưới "Lịch nghỉ trong tuần", bày giống lưới ở thẻ Đăng ký: bảy cột là bảy ngày, trục dọc là giờ, góc phải có nút thu nhỏ / phóng to lưới.',
          "Mỗi đơn là một thẻ ghi tên người nghỉ, khung giờ và tình trạng; đơn nghỉ cả ngày phủ kín cột, đơn theo giờ chỉ dài đúng quãng xin nghỉ.",
          'Dưới số ngày ở đầu mỗi cột là số đơn nghỉ của ngày đó. Trên điện thoại lưới hiện từng ngày, chọn ngày ở dải ngay trên lưới.',
          'Bấm một thẻ để mở hộp chi tiết: lý do, bộ phận, cơ sở, lời ghi kèm khi đơn được xử, cùng nút "Rút đơn" hoặc "Duyệt", "Từ chối" khi đơn đó còn việc để bạn làm.',
        ],
        howItWorks: [
          "Đây là mô hình NGƯỢC với thẻ đăng ký: ngày nào bạn không khai nghỉ thì máy coi như bạn đi làm được và có thể giao suất.",
          "Muốn chắc chắn không bị giao suất thì phải khai nghỉ — im lặng không đủ.",
          "Nếu trang của bạn chỉ có thẻ này thì đây là toàn bộ việc bạn cần làm ở đây: không phải khai ca hằng tuần, máy đã tính bạn có mặt sẵn.",
          "Không tích ô khung giờ thì đơn được hiểu là nghỉ cả ngày.",
          "Khai khung giờ thì bạn chỉ vắng đúng quãng đó; ngoài quãng, máy vẫn giao việc cho bạn như thường.",
          "Một ngày khai được nhiều quãng rời nhau, miễn các quãng không đè lên nhau.",
          "Đơn còn chờ thì lịch CHƯA đổi: máy vẫn giao suất cho tới khi đơn được xử.",
          "Đơn nằm chờ quá 24 giờ mà chưa ai xử thì tự được nhận — trừ khi ngày đó bạn còn giữ suất, khi đó đơn vẫn chờ tới khi suất được trả ra (áp dụng khi bản mới lên web).",
          "Đơn được nhận rồi thì máy thôi giao việc trong quãng bạn đã khai.",
          "Ngày bạn còn đang giữ suất thì không khai nghỉ thẳng được — phải trả suất ra trước, kể cả khi chỉ định nghỉ một lúc.",
          "Hai cách khai sống song song trên cùng trang: người khai ca từng tuần vẫn dùng thẻ đăng ký, không phải ai cũng chỉ khai nghỉ.",
          "Lưới đi theo nút đổi tuần ở đầu trang, cùng tuần với thẻ Đăng ký — trang mở sẵn ở tuần sau, muốn xem tuần này thì bấm \"Tuần này\".",
          "Lưới chỉ bày đơn đang chờ và đơn đã duyệt; đơn bị từ chối hay đã rút không còn là người vắng nên không hiện trên lưới.",
          "Người xử đơn thấy trên lưới đơn của cả khối mình; người khác chỉ thấy đơn của chính mình.",
          'Mục "Chờ tôi duyệt" không đi theo tuần: mọi đơn còn chờ từ hôm nay trở đi đều nằm ở đó, kể cả đơn của tuần khác tuần đang xem trên lưới.',
        ],
        statuses: [
          { label: "Đơn nghỉ chờ duyệt", meaning: "Đã gửi, chưa ai xử; lịch chưa đổi." },
          {
            label: "Đơn nghỉ đã duyệt",
            meaning: "Máy không giao việc cho bạn trong quãng đã khai — cả ngày, hoặc đúng khung giờ ghi trên đơn.",
          },
          { label: "Bị từ chối", meaning: "Đơn không được nhận; lý do ghi trong hộp chi tiết. Đơn này không hiện trên lưới." },
          { label: "Đã rút", meaning: "Người gửi tự rút đơn, lịch trở về như chưa nghỉ. Đơn này không hiện trên lưới." },
          { label: "cả ngày", meaning: "Đơn này nghỉ trọn ngày, không giới hạn khung giờ; trên lưới thẻ phủ kín cột." },
        ],
        blocks: [
          {
            for: ["photo", "makeup", "sale", "cskh"],
            title: "Khai ngày nghỉ",
            steps: [
              "Chọn ngày bạn nghỉ, ghi lý do rồi gửi đơn.",
              'Chỉ vắng một lúc thì tích "Nghỉ theo khung giờ", chọn Từ giờ và Đến giờ trước khi gửi.',
              "Nghỉ trọn ngày thì để nguyên, không cần tích gì.",
              "Sáng bận một quãng, chiều bận một quãng khác thì gửi hai đơn riêng cho cùng ngày.",
              "Theo dõi tới khi đơn chuyển sang đã duyệt — chờ thì lịch chưa đổi.",
              'Đổi ý thì bấm vào đơn của mình trên lưới tuần rồi bấm "Rút đơn".',
              "Không khai gì thì ngày đó bạn vẫn được tính là đi làm được.",
            ],
            troubles: [
              'Gửi đơn mà hiện dòng đỏ kèm tên khách: ngày đó bạn còn suất đang giữ. Mở "Suất của tôi", trả suất ra, rồi khai nghỉ lại.',
              "Báo khung giờ đè lên đơn khác: trong ngày bạn đã có một đơn phủ quãng này rồi — xem lại đơn của mình trên lưới tuần, rút đơn cũ hoặc chọn quãng khác.",
              "Nút gửi mờ đi: giờ bắt đầu đang đứng sau giờ kết thúc, chọn lại hai ô giờ.",
              "Không thấy giờ mình cần trong ô chọn: giờ đó nằm ngoài giờ làm việc, khai sát mép trong giờ là đủ.",
              'Gửi đơn xong mà không thấy trên lưới: lưới đang ở tuần khác ngày bạn nghỉ — bấm mũi tên đổi tuần hoặc "Tuần này".',
              "Hộp chi tiết không có nút rút: đơn đã bị từ chối, đã rút, hoặc ngày nghỉ đã qua.",
              "Đơn còn chờ mà đã tới ngày nghỉ: gấp thì nhắn quản lý trực tiếp, đừng tự nghỉ khi đơn chưa được nhận.",
              "Khai nghỉ rồi vẫn bị giao suất: xem lại đơn còn ở chặng chờ hay đã được nhận, và xem việc được giao có rơi ngoài quãng bạn khai không.",
            ],
          },
          {
            for: ["assistant"],
            title: "Khai ngày nghỉ",
            steps: [
              "Chọn ngày bạn nghỉ, ghi lý do rồi gửi đơn.",
              'Chỉ vắng một lúc thì tích "Nghỉ theo khung giờ", chọn Từ giờ và Đến giờ trước khi gửi.',
              "Nghỉ trọn ngày thì để nguyên, không cần tích gì.",
              "Sáng bận một quãng, chiều bận một quãng khác thì gửi hai đơn riêng cho cùng ngày.",
              "Theo dõi tới khi đơn chuyển sang đã duyệt — chờ thì lịch chưa đổi.",
              'Đổi ý thì bấm vào đơn của mình trên lưới tuần rồi bấm "Rút đơn".',
              "Ngày đã khai ca mà nghỉ thì huỷ ca đó luôn, đừng để lịch ghi một đằng người có mặt một nẻo.",
            ],
            troubles: [
              "Báo khung giờ đè lên đơn khác: trong ngày bạn đã có một đơn phủ quãng này rồi — xem lại đơn của mình trên lưới tuần, rút đơn cũ hoặc chọn quãng khác.",
              "Nút gửi mờ đi: giờ bắt đầu đang đứng sau giờ kết thúc, chọn lại hai ô giờ.",
              "Không thấy giờ mình cần trong ô chọn: giờ đó nằm ngoài giờ làm việc, khai sát mép trong giờ là đủ.",
              'Gửi đơn xong mà không thấy trên lưới: lưới đang ở tuần khác ngày bạn nghỉ — bấm mũi tên đổi tuần hoặc "Tuần này".',
              "Đơn còn chờ mà đã tới ngày nghỉ: gấp thì nhắn quản lý trực tiếp, đừng tự nghỉ khi đơn chưa được nhận.",
            ],
          },
          {
            for: ["lead", "exec", "qc"],
            title: "Xử đơn nghỉ",
            steps: [
              'Mở thẻ này; các đơn đang chờ nằm ở mục "Chờ tôi duyệt".',
              'Đọc khung giờ ngay trên dòng đơn: "cả ngày" là vắng trọn ngày, còn khung giờ cụ thể thì người ta chỉ vắng đúng quãng đó.',
              "Nhìn xuống lưới tuần ở cuối thẻ: đổi tới đúng tuần của đơn để xem hôm đó còn ai vắng, quãng xin nghỉ có dồn vào giờ đông khách không.",
              "Nhận đơn, hoặc không nhận kèm lý do để người gửi biết đường xoay — bấm ngay trên dòng chờ, hoặc bấm thẻ trên lưới rồi quyết trong hộp chi tiết.",
              "Nhận xong máy thôi giao việc cho người ấy trong đúng quãng đã khai.",
            ],
            troubles: [
              "Đơn chưa xử thì máy vẫn giao suất như thường — đừng để đơn nằm chờ qua ngày.",
              "Để đơn quá 24 giờ thì đơn tự được nhận và bạn nhận chuông báo; thấy không đúng thì nhắn người gửi rút đơn (áp dụng khi bản mới lên web).",
              "Chuông nhắc đơn còn chờ vì người đó còn giữ suất hôm ấy: gỡ suất ra trước rồi mới nhận đơn (áp dụng khi bản mới lên web).",
              "Cùng một người có hai đơn trong một ngày: đó là hai quãng rời nhau, quyết từng đơn một.",
              "Thấy đơn của khối khác trên lưới nhưng hộp chi tiết không có nút quyết: đơn đó thuộc khối khác khối bạn phụ trách.",
              "Lưới trống mà vẫn có đơn chờ: đơn đó thuộc tuần khác tuần đang xem — đổi tuần để thấy nó trên lưới.",
            ],
          },
        ],
      },
    ],
  },
};
